/**
 * InvoiceDeliverySection
 *
 * Shows the audit trail of invoice deliveries (#1036): each attempt's
 * channel, recipient, status, time and — on failure — the error and a retry
 * button. An e-invoice (#3071) also shows its operator and Maventa's own
 * state (handed over / delivered / confirmed / failed), and a failed one can
 * go out as an email instead.
 */

import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, Chip, Button } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import EmailIcon from '@mui/icons-material/Email';
import type { InvoiceDeliveryEntry, InvoiceDeliveryStatus } from '@/hooks/useInvoices';
import { EmailDeliveryHistory } from '@/components/email/EmailDeliveryHistory';
import { formatDateTime } from '@/utils/locale';

const STATUS_COLOR: Record<InvoiceDeliveryStatus, 'default' | 'info' | 'success' | 'error'> = {
  queued: 'default',
  processing: 'info',
  sent: 'success',
  failed: 'error',
};

/** Maventa states with a label; anything else falls back to the row status. */
const PROVIDER_STATUS_COLOR: Record<string, 'info' | 'success' | 'error'> = {
  PENDING: 'info',
  PROCESSING: 'info',
  DELIVERED: 'success',
  DELIVERY_CONFIRMED: 'success',
  FAILED: 'error',
};

const CHANNEL_LABEL_KEY: Record<string, string> = {
  email: 'delivery.channel.email',
  einvoice: 'delivery.channel.einvoice',
  reminder: 'delivery.channel.reminder',
};

interface Props {
  invoiceId: string;
  /** Receipts issued for the invoice's payments; their emails get a history of their own. */
  receiptIds?: readonly string[];
  deliveries: InvoiceDeliveryEntry[];
  canManage: boolean;
  onRetry: (deliveryId: string) => void;
  retrying: boolean;
  /** Send the invoice by email instead of a failed e-invoice. */
  onSendAsEmail?: () => void;
  sendingAsEmail?: boolean;
}

function statusChip(d: InvoiceDeliveryEntry): { color: 'default' | 'info' | 'success' | 'error'; labelKey: string } {
  // A sent e-invoice is only handed to the operator; Maventa's state says
  // whether it reached the recipient. A failed row is failed whatever it says.
  const provider = d.providerStatus ?? null;
  if (d.channel === 'einvoice' && d.status === 'sent' && provider !== null && provider in PROVIDER_STATUS_COLOR) {
    return { color: PROVIDER_STATUS_COLOR[provider] ?? 'info', labelKey: `delivery.providerStatus.${provider}` };
  }
  return { color: STATUS_COLOR[d.status] ?? 'default', labelKey: `delivery.status.${d.status}` };
}

export function InvoiceDeliverySection({
  invoiceId,
  receiptIds = [],
  deliveries,
  canManage,
  onRetry,
  retrying,
  onSendAsEmail,
  sendingAsEmail = false,
}: Props) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const hasEmail = deliveries.some((d) => d.channel === 'email');

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t('delivery.title')}
      </Typography>
      <Stack spacing={1.5}>
        {deliveries.map((d) => {
          const chip = statusChip(d);
          const channelKey = CHANNEL_LABEL_KEY[d.channel];
          const recipient = d.channel === 'einvoice' ? d.recipientEia : d.recipientEmail;
          const failed = d.status === 'failed';
          return (
            <Box
              key={d.id}
              data-testid="invoice-delivery-row"
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" color={chip.color} label={t(chip.labelKey)} />
                  {channelKey ? (
                    <Chip size="small" variant="outlined" label={t(channelKey)} />
                  ) : null}
                  {recipient ? <Typography variant="body2" noWrap>{recipient}</Typography> : null}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(d.createdAt)}
                  {d.attemptCount > 1 ? ` · ${t('delivery.attempts', { count: d.attemptCount })}` : ''}
                  {d.channel === 'einvoice' && d.recipientOperator
                    ? ` · ${t('delivery.operator', { operator: d.recipientOperator })}`
                    : ''}
                </Typography>
                {failed && d.errorMessage ? (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                    {d.errorMessage}
                  </Typography>
                ) : null}
              </Box>
              {canManage && failed && (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<ReplayIcon />}
                    onClick={() => onRetry(d.id)}
                    disabled={retrying}
                  >
                    {t('delivery.retry')}
                  </Button>
                  {d.channel === 'einvoice' && onSendAsEmail ? (
                    <Button
                      size="small"
                      startIcon={<EmailIcon />}
                      onClick={onSendAsEmail}
                      disabled={sendingAsEmail}
                      data-testid="invoice-delivery-send-as-email"
                    >
                      {t('delivery.sendAsEmail')}
                    </Button>
                  ) : null}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
      {/* What the mail provider reports, beside the attempt log above (#3787). */}
      {(hasEmail || receiptIds.length > 0) && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {hasEmail && <EmailDeliveryHistory entityType="invoice" entityId={invoiceId} title={tc('emailDelivery.invoiceTitle')} />}
          {receiptIds.map((receiptId) => (
            <EmailDeliveryHistory key={receiptId} entityType="receipt" entityId={receiptId} title={tc('emailDelivery.receiptTitle')} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
